/**
 * HPQ PDP v4 — hpqCart integration
 *
 * 5 responsibilities:
 *   1. Custom price display (with tier crossout at 3+)
 *   2. Tier banner ("Buy 3+ sauces - $11.99 each")
 *   3. Add to Cart stepper (instant via hpqCart write-behind)
 *   4. Category counting (cached Set<variantId>)
 *   5. Flavor picker with in-place product swap (Idiomorph DOM morph)
 *
 * Cart mutations via window.hpqCart (0ms local state, background Shopify sync).
 * ES5+ (uses Set, Promise). No arrow functions, no const/let, no template literals.
 */
(function () {
  'use strict';

  // product_type -> the collection whose products are its flavor siblings. This is a
  // catalog fact, not the discount rule — the rule lives in #hpq-tier-rules (emitted by
  // snippets/hpq-tier-rules.liquid, the single source) and is read into tierRules below.
  var TYPE_MAP = {
    'Hot Sauce': { handle: 'sauces' },
    'Noodles':   { handle: 'noodles' },
    'Soup Base': { handle: 'hotpot' }
  };

  // { "Hot Sauce": { min: 3, off: 200, label: "sauces" }, ... }
  //
  // Delta-shaped on purpose. Shopify's discounts are fixed_amount / allocation_method
  // "each" (measured 2026-08-23 off line_level_discount_allocations), so the saving is
  // per unit, not a destination price. Subtracting keeps the PDP honest if a product in
  // the category is ever priced differently from its siblings; a stored tier price would
  // quote $11.99 while checkout charged $13.99. Never reintroduce a literal price here.
  var tierRules = null;

  // Classifies the way Liquid does — exact key first, then the rule's own `match`
  // substrings — so a product typed 'Chili Sauce' resolves here exactly as it does
  // server-side. Keying on the exact product_type alone was the second half of the
  // duplicated category map.
  function tierFor(productType) {
    if (!tierRules || !productType) return null;
    var exact = tierRules[productType];
    if (exact && typeof exact.off === 'number' && typeof exact.min === 'number') return exact;
    var lower = String(productType).toLowerCase();
    for (var key in tierRules) {
      if (!Object.prototype.hasOwnProperty.call(tierRules, key)) continue;
      var r = tierRules[key];
      if (!r || typeof r.off !== 'number' || typeof r.min !== 'number' || !r.match) continue;
      for (var i = 0; i < r.match.length; i++) {
        if (lower.indexOf(String(r.match[i]).toLowerCase()) !== -1) return r;
      }
    }
    return null;
  }

  function loadTierRules() {
    var el = document.getElementById('hpq-tier-rules');
    if (!el) return;
    try { tierRules = JSON.parse(el.textContent); } catch (e) { tierRules = null; }
  }

  function money(cents) {
    return '$' + (Math.max(0, cents) / 100).toFixed(2);
  }

  // Fallback pill labels. The live source is the hpq.flavor_name metafield, bridged into
  // window.HPQ_CONFIG.flavorNames by snippets/theme-globals.liquid — edit the product in
  // Shopify admin, not this map. Kept as a safety net for when the bridge is absent
  // (metafield unset on a new product, or the snippet not yet deployed).
  var FLAVOR_NAMES = {
    'crunchy-garlic-chili-crisp': 'Crunchy Garlic',
    'wild-mushroom-chili-sauce': 'Wild Mushroom',
    'toasted-seed-chili-crunch': 'Toasted Seed',
    'prickly-pepper-chili-crisp': 'Prickly Pepper',
    'spicy-hotpot-thick-cut-noodles': 'Spicy Hotpot',
    'spicy-sesame-dan-dan-noodles': 'Spicy Sesame',
    'roasted-chili-noodles': 'Roasted Chili',
    'wild-mushroom-chili-crisp-ramen': 'Wild Mushroom',
    'sichuan-mala-hotpot-soup-base': 'Sichuan Mala',
    'savory-mushroom-hotpot-soup-base': 'Savory Mushroom'
  };

  var state = {
    handle: '',
    variantId: '',
    sectionId: '',
    product: null,
    category: null,
    categoryProducts: null,
    categoryVariantIds: null,
    count: 0,
    cart: null,
    swapping: false
  };

  var dom = {};

  // ── Init ───────────────────────────────────────────────────────────

  function init() {
    if (!document.body.classList.contains('template-product')) return;

    loadTierRules();

    var form = document.querySelector('[data-product-form]');
    if (!form) return;
    if (!window.hpqCart) return;

    state.handle = form.getAttribute('data-product-handle') || '';
    state.variantId = form.getAttribute('data-current-product-id') || '';
    if (!state.variantId) {
      var sel = form.querySelector('select[name="id"]');
      if (sel) state.variantId = sel.value;
    }
    if (!state.variantId) return;
    state.variantId = String(state.variantId);

    var sectionEl = document.querySelector('[data-section-id][data-section-type="product"]') ||
                    document.querySelector('.product[data-section-id]');
    state.sectionId = sectionEl ? sectionEl.getAttribute('data-section-id') : '';

    cleanup();

    var productFetch = fetch('/products/' + encodeURIComponent(state.handle) + '.json')
      .then(function (r) { return r.json(); });
    var cartReady = window.hpqCart.isLoaded()
      ? Promise.resolve()
      : window.hpqCart.hydrate();

    Promise.all([productFetch, cartReady]).then(function (results) {
      state.product = results[0].product;
      state.category = TYPE_MAP[state.product.product_type || ''] || null;
      state.cart = window.hpqCart.getCart();

      renderAll(state.cart);

      if (state.category) {
        fetch('/collections/' + state.category.handle + '/products.json')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            state.categoryProducts = data.products;
            state.categoryVariantIds = new Set();
            data.products.forEach(function (p) {
              p.variants.forEach(function (v) {
                state.categoryVariantIds.add(String(v.id));
              });
            });
            renderFlavorPicker(data.products);
            updateFlavorBadges(state.cart);
            updatePrice(state.cart);
            prefetchSiblings(data.products);
          })
          .catch(function () {});
      }
    }).catch(function () {});
  }

  function cleanup() {
    var old = document.querySelectorAll('.hpq-injected-price, .hpq-injected-tier, .hpq-injected-flavors, .hpq-pdp-atc');
    for (var i = 0; i < old.length; i++) {
      var block = old[i].closest('.product__block') || old[i];
      if (block.parentNode) block.parentNode.removeChild(block);
    }
  }

  // ── renderAll — single synchronous DOM pass ────────────────────────

  function renderAll(cart) {
    var header = document.querySelector('.product__block--product-header');
    if (!header) return;
    var anchor = header.closest('.product__block') || header;

    var stilettoPrice = document.querySelector('.product__price');
    if (stilettoPrice) stilettoPrice.style.setProperty('display', 'none', 'important');

    var priceEl = buildPrice();
    anchor.insertAdjacentElement('afterend', priceEl);
    anchor = priceEl;

    var tierEl = buildTierBanner();
    if (tierEl) {
      anchor.insertAdjacentElement('afterend', tierEl);
      anchor = tierEl;
    }

    if (state.category) {
      var flavorSlot = document.createElement('div');
      flavorSlot.className = 'product__block product__block--medium hpq-injected-flavors';
      flavorSlot.id = 'hpq-flavor-slot';
      anchor.insertAdjacentElement('afterend', flavorSlot);
    }

    var invBlock = document.querySelector('.inventory-counter');
    var stepperAnchor = invBlock
      ? (invBlock.closest('.product__block') || invBlock)
      : anchor;
    var stepperEl = buildStepper();
    stepperAnchor.insertAdjacentElement('afterend', stepperEl);

    hideStilettoControls();
    fixInventory(document);
    onCartChange(cart);
  }

  // ── Price ──────────────────────────────────────────────────────────

  function buildPrice() {
    var baseCents = Math.round((parseFloat(state.product.variants[0].price) || 0) * 100);
    var price = money(baseCents);
    var rule = tierFor(state.product.product_type);
    var el = document.createElement('div');
    el.className = 'product__block product__block--medium hpq-injected-price';
    el.innerHTML =
      '<div class="hpq-pdp-price" data-hpq-price data-base-price="' + price + '"' +
      (rule ? ' data-tier-price="' + money(baseCents - rule.off) + '"' +
              ' data-tier-min="' + rule.min + '"' : '') + '>' +
      '<span class="hpq-pdp-price__current">' + price + '</span>' +
      '<span class="hpq-pdp-price__original" style="display:none"></span>' +
      '</div>';
    dom.priceWrap = el.querySelector('[data-hpq-price]');
    dom.priceCurrent = el.querySelector('.hpq-pdp-price__current');
    dom.priceOriginal = el.querySelector('.hpq-pdp-price__original');
    return el;
  }

  function updatePrice(cart) {
    if (!dom.priceWrap) return;
    var base = dom.priceWrap.getAttribute('data-base-price');
    var tier = dom.priceWrap.getAttribute('data-tier-price');
    var tierMin = parseInt(dom.priceWrap.getAttribute('data-tier-min'), 10);
    var catCount = getCategoryCount(cart);

    if (tier && tierMin > 0 && catCount >= tierMin) {
      dom.priceCurrent.textContent = tier;
      dom.priceCurrent.classList.add('hpq-pdp-price__current--tier');
      dom.priceOriginal.textContent = base;
      dom.priceOriginal.style.display = 'inline';
    } else {
      dom.priceCurrent.textContent = base;
      dom.priceCurrent.classList.remove('hpq-pdp-price__current--tier');
      dom.priceOriginal.style.display = 'none';
    }
  }

  // ── Tier banner ────────────────────────────────────────────────────

  function buildTierBanner() {
    var rule = tierFor(state.product.product_type);
    if (!rule) return null;
    var baseCents = Math.round((parseFloat(state.product.variants[0].price) || 0) * 100);
    var tierPrice = money(baseCents - rule.off);
    var percent = baseCents > 0 ? Math.round((rule.off * 100) / baseCents) : 0;
    var el = document.createElement('div');
    el.className = 'product__block product__block--medium hpq-injected-tier';
    el.innerHTML =
      '<div class="hpq-tier-pricing"><div class="hpq-tier-pricing__inner">' +
      '<div class="hpq-tier-pricing__icon">' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>' +
      '<line x1="7" y1="7" x2="7.01" y2="7"/></svg></div>' +
      '<div class="hpq-tier-pricing__text">' +
      '<span class="hpq-tier-pricing__headline">Buy ' + rule.min + '+ ' + (rule.label || '') + ' \u2014 ' + tierPrice + ' each</span>' +
      '<span class="hpq-tier-pricing__detail">Save ' + percent + '% \u00b7 Mix any flavors \u00b7 Applied at checkout</span>' +
      '</div></div></div>';
    return el;
  }

  // ── Flavor picker ─────────────────────────────────────────────────

  function renderFlavorPicker(products) {
    var slot = document.getElementById('hpq-flavor-slot');
    if (!slot || !products || !products.length) return;

    var html = '<div class="hpq-flavors"><div class="hpq-flavors__label" id="hpq-flavors-label">Flavors</div>' +
               '<div class="hpq-flavors__options" role="radiogroup" aria-labelledby="hpq-flavors-label">';

    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var isCurrent = (p.handle === state.handle);
      var img = p.images[0] ? p.images[0].src.replace(/\.(jpg|png|webp)/i, '_100x100.$1') : '';
      var name = shortenTitle(p.title, p.handle);
      var vid = p.variants[0] ? String(p.variants[0].id) : '';

      html += '<button type="button" class="hpq-flavors__opt' + (isCurrent ? ' hpq-flavors__opt--active' : '') +
              '" data-hpq-swap="' + esc(p.handle) + '" data-hpq-variant="' + esc(vid) + '" role="radio" aria-checked="' + (isCurrent ? 'true' : 'false') + '">';
      html += '<span class="hpq-flavors__badge" style="display:none"></span>';
      if (img) {
        html += '<img class="hpq-flavors__img" src="' + esc(img) + '" alt="" width="48" height="48" loading="lazy">';
      }
      html += '<span class="hpq-flavors__name">' + esc(name) + '</span></button>';
    }

    html += '</div></div>';
    slot.innerHTML = html;

    // Delegate click handler for swap buttons
    slot.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-hpq-swap]');
      if (!btn || state.swapping) return;
      var handle = btn.getAttribute('data-hpq-swap');
      if (handle && handle !== state.handle) {
        btn.classList.add('hpq-flavors__opt--loading');
        swapProduct(handle);
      }
    });
  }

  // ── Product swap (Idiomorph) ──────────────────────────────────────

  function swapProduct(handle) {
    if (!state.sectionId || !window.Idiomorph) {
      window.location.href = '/products/' + handle;
      return;
    }

    state.swapping = true;

    // Detach our injected elements (they'll be re-created after morph)
    var tierEl = document.querySelector('.hpq-injected-tier');
    var flavorsEl = document.querySelector('.hpq-injected-flavors');
    var priceEl = document.querySelector('.hpq-injected-price');
    var stepperWrap = document.querySelector('.hpq-pdp-atc');
    if (stepperWrap) stepperWrap = stepperWrap.closest('.product__block');

    // Preserve tier + flavors (category-level, don't re-fetch)
    if (tierEl && tierEl.parentNode) tierEl.parentNode.removeChild(tierEl);
    if (flavorsEl && flavorsEl.parentNode) flavorsEl.parentNode.removeChild(flavorsEl);
    if (priceEl && priceEl.parentNode) priceEl.parentNode.removeChild(priceEl);
    if (stepperWrap && stepperWrap.parentNode) stepperWrap.parentNode.removeChild(stepperWrap);

    // Fetch new section HTML + product data in parallel
    var sectionFetch = fetch('/products/' + handle + '?section_id=' + state.sectionId)
      .then(function (r) { return r.text(); });
    var productFetch = fetch('/products/' + encodeURIComponent(handle) + '.json')
      .then(function (r) { return r.json(); });
    // Discover sibling sections to re-render alongside the main section.
    // Use the FULL data-section-id (template--XXXX__suffix) — Shopify's
    // ?sections= API requires the template-prefixed ID to find the right
    // template-instance with its block configuration. The bare suffix
    // returns the section without blocks (empty tabs) or null for
    // template-only IDs like "1777088501a5475f0b".
    var SKIP_SUFFIXES = ['hpq-complete-your-meal'];
    var siblingEntries = [];
    var allSectionEls = document.querySelectorAll('main [data-section-id]');
    for (var si = 0; si < allSectionEls.length; si++) {
      var rawId = allSectionEls[si].getAttribute('data-section-id');
      if (rawId === state.sectionId) continue;
      var suffix = rawId.replace(/^template--\d+__/, '');
      if (SKIP_SUFFIXES.indexOf(suffix) !== -1) continue;
      siblingEntries.push({ id: rawId, el: allSectionEls[si] });
    }

    var siblingFetch = siblingEntries.length > 0
      ? fetch('/products/' + encodeURIComponent(handle) + '?sections=' + siblingEntries.map(function (e) { return e.id; }).join(','))
          .then(function (r) { return r.json(); })
      : Promise.resolve({});

    Promise.all([sectionFetch, productFetch, siblingFetch]).then(function (results) {
      var html = results[0];
      var productData = results[1];
      var siblingHtmlMap = results[2];
      var container = document.querySelector('[data-section-id="' + state.sectionId + '"]');

      if (!container) {
        window.location.href = '/products/' + handle;
        return;
      }

      // Parse new HTML
      var temp = document.createElement('div');
      temp.innerHTML = html;
      var newContent = temp.querySelector('[data-section-id="' + state.sectionId + '"]') || temp.firstElementChild;
      if (!newContent) {
        window.location.href = '/products/' + handle;
        return;
      }

      // Morph the section container — preserves gallery JS bindings
      Idiomorph.morph(container, newContent, {
        morphStyle: 'innerHTML',
        ignoreActiveValue: true
      });

      // Update state
      state.handle = handle;
      state.product = productData.product;
      state.category = TYPE_MAP[state.product.product_type || ''] || null;
      state.count = 0;

      // Update URL + title
      history.pushState({}, '', '/products/' + handle);
      var titleEl = container.querySelector('.product__title, h1');
      if (titleEl) document.title = titleEl.textContent.trim() + ' \u2013 Hotpot Queen';

      // Re-read variant ID from morphed form
      var form = container.querySelector('[data-product-form]');
      if (form) {
        state.variantId = form.getAttribute('data-current-product-id') || '';
        if (!state.variantId) {
          var sel = form.querySelector('select[name="id"]');
          if (sel) state.variantId = sel.value;
        }
        state.variantId = String(state.variantId);
      }

      // Re-inject our custom elements on the morphed DOM
      var header = container.querySelector('.product__block--product-header');
      if (header) {
        var anchor = header.closest('.product__block') || header;

        var stilettoPrice = container.querySelector('.product__price');
        if (stilettoPrice) stilettoPrice.style.setProperty('display', 'none', 'important');

        var newPriceEl = buildPrice();
        anchor.insertAdjacentElement('afterend', newPriceEl);
        anchor = newPriceEl;

        // Rebuild the tier banner rather than re-inserting the preserved node. Now that the
        // tier price is derived (variant price minus the per-unit delta) it is a function of
        // the product being shown, so a swap to a differently-priced sibling must re-render
        // it. Preserving the old node would quote the previous flavor's price.
        var newTierEl = buildTierBanner();
        if (newTierEl) {
          anchor.insertAdjacentElement('afterend', newTierEl);
          anchor = newTierEl;
        }

        // Re-insert preserved flavor picker (update active state)
        if (flavorsEl) {
          anchor.insertAdjacentElement('afterend', flavorsEl);
          updateFlavorActive(handle);
        }
      }

      // Re-inject stepper after inventory
      var invBlock = container.querySelector('.inventory-counter');
      var stepAnchor = invBlock ? (invBlock.closest('.product__block') || invBlock) : header;
      if (!stepAnchor) return;
      var newStepper = buildStepper();
      stepAnchor.insertAdjacentElement('afterend', newStepper);

      hideStilettoControls(container);

      // Restore inventory text from morphed DOM's JSON data
      fixInventory(container);

      // Re-render sibling sections (product-tabs, reviews, flavor-overlay-data, etc.)
      // The ?sections= API wraps each section's content in <div class="shopify-section">,
      // so we must query for the inner [data-section-id] element to morph against — otherwise
      // we'd nest the section inside itself and break its CSS scoping.
      for (var sj = 0; sj < siblingEntries.length; sj++) {
        var entry = siblingEntries[sj];
        var newSiblingHtml = siblingHtmlMap[entry.id];
        if (!newSiblingHtml) continue;
        var tempSib = document.createElement('div');
        tempSib.innerHTML = newSiblingHtml;
        var sibSelector = '[data-section-id="' + entry.el.getAttribute('data-section-id') + '"]';
        var matchedNew = tempSib.querySelector(sibSelector);
        if (!matchedNew) {
          // Fallback for sections that may render with a different data-section-id
          // (theme-id mismatch between local and shopify): take any [data-section-id]
          // descendant of the .shopify-section wrapper.
          matchedNew = tempSib.querySelector('[data-section-id]');
        }
        if (!matchedNew) continue;
        // outerHTML morphStyle replaces entry.el with matchedNew (preserving
        // diffing/focus where possible). The previous innerHTML morphStyle
        // nests matchedNew as a single child of entry.el — Idiomorph treats
        // the new node AS the new contents, not "copy its children", so each
        // swap added another nesting layer. That collapsed the apps section
        // (Judge.me) to height 0 and progressively shrank product-tabs.
        Idiomorph.morph(entry.el, matchedNew, { morphStyle: 'outerHTML' });
        // outerHTML morph leaves entry.el detached — re-locate the live node
        // by data-section-id for the script rehydration step below.
        var liveEl = document.querySelector('[data-section-id="' + entry.id + '"]');
        if (!liveEl) continue;
        // Stiletto fade-up animations gate visibility with .is-visible.
        // Stiletto adds it via IntersectionObserver during section onLoad,
        // but the morphed-in nodes are fresh so the observer never fired
        // for them. Without is-visible, .animation--item stays at
        // opacity:0 + translateY(40px) — i.e. content is mounted but
        // invisible. Force it on the morphed root + all .animation
        // descendants so swapped sections paint immediately.
        if (liveEl.classList.contains('animation')) liveEl.classList.add('is-visible');
        var animEls = liveEl.querySelectorAll('.animation, .animation--item');
        for (var ae = 0; ae < animEls.length; ae++) animEls[ae].classList.add('is-visible');

        // innerHTML/Idiomorph leaves <script> tags inert. Replace each with
        // a fresh element so the browser evaluates it. Required for the
        // Judge.me per-product data-registration script
        // (<script class="jdgm-review-widget-data">) and any {% javascript %}
        // blocks Stiletto sections may carry.
        var scripts = liveEl.querySelectorAll('script');
        for (var sc = 0; sc < scripts.length; sc++) {
          var oldScript = scripts[sc];
          var newScript = document.createElement('script');
          for (var an = 0; an < oldScript.attributes.length; an++) {
            newScript.setAttribute(oldScript.attributes[an].name, oldScript.attributes[an].value);
          }
          newScript.textContent = oldScript.textContent;
          if (oldScript.parentNode) {
            oldScript.parentNode.replaceChild(newScript, oldScript);
          }
        }
      }

      // Dispatch so hpq-flavor-overlay.js re-reads the freshly rendered JSON island
      document.dispatchEvent(new CustomEvent('hpq:product:swapped'));

      // Re-init Judge.me reviews widget. The exact API surface differs across
      // Judge.me versions; try every known hook, then strip the temp-hiding
      // style as a last-resort reveal so the server-rendered legacy widget
      // is visible even if the JS runtime can't be coaxed to remount.
      try { if (typeof window.jdgmSetup === 'function') window.jdgmSetup(); } catch (e) {}
      try { if (typeof window.jdgmRefreshAll === 'function') window.jdgmRefreshAll(); } catch (e) {}
      try { if (window.jdgm && typeof window.jdgm.init === 'function') window.jdgm.init(); } catch (e) {}
      try {
        if (window.jdgm && window.jdgm.JdgmRev && typeof window.jdgm.JdgmRev.initRevPagi === 'function') {
          window.jdgm.JdgmRev.initRevPagi();
        }
      } catch (e) {}
      try { document.dispatchEvent(new Event('jdgm:refresh')); } catch (e) {}
      try { window.dispatchEvent(new Event('jdgm:refresh')); } catch (e) {}
      // Re-mount Judge.me's Vue 3 "revamp" widget. The ?sections= API only
      // returns the legacy SSR HTML — Vue mounts client-side via a module
      // script (loader.js → review_widget.js). After morph, the widget is
      // back to legacy markup. Re-injecting the entry-point module with a
      // cache-bust forces a fresh import → fresh findAndInitializeWidgets()
      // → Vue mounts on the new element. The legacy unhide is kept as a
      // belt-and-suspenders fallback in case the Vue script can't load
      // (offline / blocked CDN etc.).
      try {
        var unmountedJdgm = document.querySelectorAll('.jdgm-widget[data-entry-point]:not([data-v-app])');
        if (unmountedJdgm.length > 0) {
          var existingEntry = document.querySelector('script[type="module"][src*="review_widget"]') ||
                              document.querySelector('script[src*="review_widget.js"]');
          if (existingEntry && existingEntry.src) {
            var s = document.createElement('script');
            s.type = 'module';
            s.src = existingEntry.src.split('?')[0] + '?hpq_remount=' + Date.now();
            document.head.appendChild(s);
          }
        }
        // Legacy fallback reveal (only matters if the Vue mount script fails
        // to load — Vue replaces .jdgm-legacy-widget-content entirely on
        // success, so these mutations are harmless then).
        var jdgmHiders = document.querySelectorAll('.jdgm-temp-hiding-style');
        for (var jh = 0; jh < jdgmHiders.length; jh++) jdgmHiders[jh].remove();
        var jdgmLegacy = document.querySelectorAll('.jdgm-legacy-widget-content');
        for (var jl = 0; jl < jdgmLegacy.length; jl++) jdgmLegacy[jl].style.display = 'block';
        var jdgmRevs = document.querySelectorAll('.jdgm-rev');
        for (var jr = 0; jr < jdgmRevs.length; jr++) jdgmRevs[jr].classList.add('jdgm--leex-done-setup');
        var jdgmRevWidgs = document.querySelectorAll('.jdgm-review-widget');
        for (var jw = 0; jw < jdgmRevWidgs.length; jw++) jdgmRevWidgs[jw].classList.add('jdgm--leex-done-setup-widget');
      } catch (e) {}

      // Re-init Stiletto product-tabs section
      if (window.Shopify && window.Shopify.theme && window.Shopify.theme.sections) {
        var tabInstances = window.Shopify.theme.sections.instances;
        for (var tk = 0; tk < tabInstances.length; tk++) {
          if (tabInstances[tk].type === 'product-tabs') {
            if (typeof tabInstances[tk].onUnload === 'function') tabInstances[tk].onUnload();
            if (typeof tabInstances[tk].onLoad === 'function') tabInstances[tk].onLoad();
            break;
          }
        }
      }

      // Re-initialize Stiletto's product section (gallery, thumbnails, etc.)
      reinitStilettoProduct();

      // Sync stepper with current cart state (instant from hpqCart)
      onCartChange(window.hpqCart.getCart());

      state.swapping = false;

      var activeOpt = document.querySelector('.hpq-flavors__opt--active');
      if (activeOpt) activeOpt.focus();

    }).catch(function () {
      state.swapping = false;
      window.location.href = '/products/' + handle;
    });
  }

  function updateFlavorActive(activeHandle) {
    var opts = document.querySelectorAll('.hpq-flavors__opt');
    for (var i = 0; i < opts.length; i++) {
      var h = opts[i].getAttribute('data-hpq-swap');
      var isActive = (h === activeHandle);
      opts[i].classList.toggle('hpq-flavors__opt--active', isActive);
      opts[i].classList.remove('hpq-flavors__opt--loading');
      opts[i].setAttribute('aria-checked', isActive ? 'true' : 'false');
    }
  }

  function updateFlavorBadges(cart) {
    if (!cart || !cart.items) return;
    // Build variant→quantity map from cart
    var qtyMap = {};
    for (var i = 0; i < cart.items.length; i++) {
      qtyMap[String(cart.items[i].variant_id)] = (qtyMap[String(cart.items[i].variant_id)] || 0) + cart.items[i].quantity;
    }
    // Update each pill's badge
    var opts = document.querySelectorAll('.hpq-flavors__opt');
    for (var j = 0; j < opts.length; j++) {
      var vid = opts[j].getAttribute('data-hpq-variant');
      var badge = opts[j].querySelector('.hpq-flavors__badge');
      if (!badge) continue;
      var qty = vid ? (qtyMap[vid] || 0) : 0;
      if (qty > 0) {
        badge.textContent = qty;
        badge.style.display = '';
        opts[j].classList.add('hpq-flavors__opt--in-cart');
      } else {
        badge.style.display = 'none';
        opts[j].classList.remove('hpq-flavors__opt--in-cart');
      }
    }
  }

  function prefetchSiblings(products) {
    // Prefetch sibling section renders on idle for instant perceived swap
    var doIt = function () {
      for (var i = 0; i < products.length; i++) {
        if (products[i].handle !== state.handle && state.sectionId) {
          var href = '/products/' + products[i].handle + '?section_id=' + state.sectionId;
          if (!document.querySelector('link[rel="prefetch"][href="' + href + '"]')) {
            var link = document.createElement('link');
            link.rel = 'prefetch';
            link.href = href;
            document.head.appendChild(link);
          }
        }
      }
    };
    if (window.requestIdleCallback) {
      requestIdleCallback(doIt);
    } else {
      setTimeout(doIt, 2000);
    }
  }

  // ── Popstate (back/forward after pushState) ───────────────────────

  window.addEventListener('popstate', function () {
    var match = window.location.pathname.match(/\/products\/([^?#/]+)/);
    if (!match || match[1] === state.handle) return;
    var handle = decodeURIComponent(match[1]);
    // Only swap to known products (prevents open redirect via crafted pushState)
    if (state.categoryProducts) {
      for (var i = 0; i < state.categoryProducts.length; i++) {
        if (state.categoryProducts[i].handle === handle) {
          swapProduct(handle);
          return;
        }
      }
    }
  });

  // ── Category counting ────────────────────────────────────────────

  function getCategoryCount(cart) {
    if (!state.categoryVariantIds || !cart || !cart.items) return 0;
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (state.categoryVariantIds.has(String(cart.items[i].variant_id))) {
        total += cart.items[i].quantity;
      }
    }
    return total;
  }

  // ── Stepper ──────────────────────────────────────────────────────

  function isSelectedAvailable() {
    if (state.categoryProducts) {
      for (var i = 0; i < state.categoryProducts.length; i++) {
        var v = state.categoryProducts[i].variants && state.categoryProducts[i].variants[0];
        if (v && String(v.id) === state.variantId) return v.available !== false;
      }
    }
    var cfg = window._ABConfig && window._ABConfig.product;
    if (cfg && cfg.variants) {
      for (var j = 0; j < cfg.variants.length; j++) {
        if (String(cfg.variants[j].id) === state.variantId) return cfg.variants[j].available !== false;
      }
    }
    return true;
  }

  function buildStepper() {
    var wrap = document.createElement('div');
    wrap.className = 'product__block product__block--medium';

    if (!state.product.variants || !state.product.variants.length || !isSelectedAvailable()) {
      wrap.innerHTML =
        '<div class="hpq-pdp-atc">' +
        '<button type="button" class="hpq-pdp-atc__btn--sold-out" disabled>Sold Out</button>' +
        '</div>';
      return wrap;
    }

    wrap.innerHTML =
      '<div class="hpq-pdp-atc">' +
      '<button type="button" class="hpq-pdp-atc__btn--add">Add to Cart</button>' +
      '<div class="hpq-pdp-atc__stepper" style="display:none">' +
      '<button type="button" class="hpq-pdp-atc__step hpq-pdp-atc__step--minus">' +
      '<svg class="hpq-pdp-atc__svg--minus" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 10h12" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/></svg>' +
      '<svg class="hpq-pdp-atc__svg--trash" width="20" height="20" viewBox="0 0 20 20" fill="none" style="display:none"><path d="M3 5h14M7 5V4a1 1 0 011-1h4a1 1 0 011 1v1M5.5 5l.5 11a1 1 0 001 1h6a1 1 0 001-1l.5-11" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '<span class="hpq-pdp-atc__count">0</span>' +
      '<button type="button" class="hpq-pdp-atc__step hpq-pdp-atc__step--plus">' +
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 4v12M4 10h12" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/></svg>' +
      '</button>' +
      '</div>' +
      '</div>';

    dom.addBtn = wrap.querySelector('.hpq-pdp-atc__btn--add');
    dom.stepper = wrap.querySelector('.hpq-pdp-atc__stepper');
    dom.countEl = wrap.querySelector('.hpq-pdp-atc__count');
    dom.minBtn = wrap.querySelector('.hpq-pdp-atc__step--minus');
    dom.plsBtn = wrap.querySelector('.hpq-pdp-atc__step--plus');
    dom.svgMinus = wrap.querySelector('.hpq-pdp-atc__svg--minus');
    dom.svgTrash = wrap.querySelector('.hpq-pdp-atc__svg--trash');

    dom.addBtn.addEventListener('click', function (e) {
      e.preventDefault();
      var priceCents = Math.round(parseFloat(state.product.variants[0].price) * 100);
      var meta = {
        image: state.product.images[0] ? state.product.images[0].src : '',
        url: '/products/' + state.handle
      };
      window.hpqCart.setQty(state.variantId, 1, priceCents, state.product.title, meta);
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { source: 'hpq-pdp' } }));
      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
    });

    dom.plsBtn.addEventListener('click', function (e) {
      e.preventDefault();
      var priceCents = Math.round(parseFloat(state.product.variants[0].price) * 100);
      var meta = {
        image: state.product.images[0] ? state.product.images[0].src : '',
        url: '/products/' + state.handle
      };
      window.hpqCart.addQty(state.variantId, 1, priceCents, state.product.title, meta);
    });

    dom.minBtn.addEventListener('click', function (e) {
      e.preventDefault();
      window.hpqCart.addQty(state.variantId, -1);
    });

    return wrap;
  }

  function showCount(n) {
    if (!dom.addBtn || !dom.stepper) return;
    state.count = n;
    if (!dom.countEl) return;
    dom.countEl.textContent = n;
    if (n > 0) {
      dom.addBtn.style.display = 'none';
      dom.stepper.style.display = 'flex';
      dom.svgMinus.style.display = n === 1 ? 'none' : 'block';
      dom.svgTrash.style.display = n === 1 ? 'block' : 'none';
    } else {
      dom.addBtn.style.display = 'flex';
      dom.stepper.style.display = 'none';
    }
  }

  // ── Cart state handler ────────────────────────────────────────────

  function onCartChange(cart) {
    if (!cart || !cart.items) return;
    state.cart = cart;

    var found = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === state.variantId) {
        found = cart.items[i].quantity;
        break;
      }
    }
    showCount(found);

    updatePrice(cart);
    updateFlavorBadges(cart);
  }

  // ── Stiletto product re-init (gallery, thumbnails, lightbox) ────

  function reinitStilettoProduct() {
    if (!window.Shopify || !window.Shopify.theme || !window.Shopify.theme.sections) return;
    var instances = window.Shopify.theme.sections.instances;
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].type === 'product') {
        if (typeof instances[i].onUnload === 'function') instances[i].onUnload();
        if (typeof instances[i].onLoad === 'function') instances[i].onLoad();
        return;
      }
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────

  function esc(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function shortenTitle(title, handle) {
    if (!handle) return title;
    // Operator-set label wins; hardcoded map is the fallback; product title is the floor.
    var bridged = window.HPQ_CONFIG && window.HPQ_CONFIG.flavorNames;
    if (bridged && bridged[handle]) return bridged[handle];
    if (FLAVOR_NAMES[handle]) return FLAVOR_NAMES[handle];
    return title;
  }

  function fixInventory(container) {
    try {
      var invC = container.querySelector('[data-inventory-counter]');
      var invJ = container.querySelector('[data-product-inventory-json]');
      if (invC && invJ) {
        var invD = JSON.parse(invJ.textContent);
        var msgEl = invC.querySelector('.inventory-counter__message');
        var barEl = invC.querySelector('.inventory-counter__bar-progress');
        var firstKey = Object.keys(invD.inventory)[0];
        if (firstKey && msgEl) {
          msgEl.textContent = invD.inventory[firstKey].inventory_message || '';
          var max = parseInt(invC.getAttribute('data-stock-countdown-max') || '100', 10);
          var threshold = parseInt(invC.getAttribute('data-low-inventory-threshold') || '0', 10);
          var qty = invD.inventory[firstKey].inventory_quantity || 0;
          var mgmt = invD.inventory[firstKey].inventory_management;
          if (barEl) {
            barEl.style.width = Math.min(100, Math.round((qty / max) * 100)) + '%';
          }
          invC.classList.remove('inventory-counter--low', 'inventory-counter--in-stock', 'inventory-counter--out');
          if (!mgmt) {
            invC.classList.add('inventory-counter--in-stock');
          } else if (qty <= 0) {
            invC.classList.add('inventory-counter--out');
          } else if (threshold > 0 && qty <= threshold) {
            invC.classList.add('inventory-counter--low');
          } else {
            invC.classList.add('inventory-counter--in-stock');
          }
        }
      }
    } catch (e) {}
  }

  function hideStilettoControls(container) {
    var root = container || document;
    var sels = '.product__controls-group-quantity, .product-form__cart-submit, .product-form__quantity';
    var els = root.querySelectorAll(sels);
    for (var i = 0; i < els.length; i++) els[i].style.setProperty('display', 'none', 'important');
  }

  // ── Global event listeners (registered once) ─────────────────────

  document.addEventListener('shopify:section:load', function () { init(); });

  document.addEventListener('cart:updated', function (e) {
    var cart = e.detail && e.detail.cart;
    if (cart && cart.items) {
      onCartChange(cart);
    }
  });

  // ── Boot ─────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
